/**
 * Advanced value, color, transform, and SVG path morph interpolator.
 */
import { Point2D } from './Transform';

export interface RGBA {
  r: number;
  g: number;
  b: number;
  a: number;
}

export class Interpolator {
  public static lerp(start: number, end: number, t: number): number {
    return start + (end - start) * t;
  }

  public static clamp(val: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, val));
  }

  public static lerpPoint(p1: Point2D, p2: Point2D, t: number): Point2D {
    return {
      x: Interpolator.lerp(p1.x, p2.x, t),
      y: Interpolator.lerp(p1.y, p2.y, t)
    };
  }

  public static parseColor(colorStr: string): RGBA {
    if (!colorStr) return { r: 0, g: 0, b: 0, a: 1 };
    colorStr = colorStr.trim();

    // Hex #RGB or #RRGGBB or #RRGGBBAA
    if (colorStr.startsWith('#')) {
      const hex = colorStr.slice(1);
      if (hex.length === 3) {
        return {
          r: parseInt(hex[0] + hex[0], 16),
          g: parseInt(hex[1] + hex[1], 16),
          b: parseInt(hex[2] + hex[2], 16),
          a: 1
        };
      }
      if (hex.length === 6) {
        return {
          r: parseInt(hex.slice(0, 2), 16),
          g: parseInt(hex.slice(2, 4), 16),
          b: parseInt(hex.slice(4, 6), 16),
          a: 1
        };
      }
      if (hex.length === 8) {
        return {
          r: parseInt(hex.slice(0, 2), 16),
          g: parseInt(hex.slice(2, 4), 16),
          b: parseInt(hex.slice(4, 6), 16),
          a: parseInt(hex.slice(6, 8), 16) / 255
        };
      }
    }

    // rgb(r, g, b) or rgba(r, g, b, a)
    if (colorStr.startsWith('rgb')) {
      const match = colorStr.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)/);
      if (match) {
        return {
          r: parseFloat(match[1]),
          g: parseFloat(match[2]),
          b: parseFloat(match[3]),
          a: match[4] !== undefined ? parseFloat(match[4]) : 1
        };
      }
    }

    // Common named colors fallback
    const named: Record<string, RGBA> = {
      transparent: { r: 0, g: 0, b: 0, a: 0 },
      white: { r: 255, g: 255, b: 255, a: 1 },
      black: { r: 0, g: 0, b: 0, a: 1 },
      red: { r: 239, g: 68, b: 68, a: 1 },
      green: { r: 34, g: 197, b: 94, a: 1 },
      blue: { r: 59, g: 130, b: 246, a: 1 },
      yellow: { r: 234, g: 179, b: 8, a: 1 },
      gold: { r: 255, g: 215, b: 0, a: 1 },
      cyan: { r: 6, g: 182, b: 212, a: 1 },
      purple: { r: 168, g: 85, b: 247, a: 1 }
    };

    if (named[colorStr.toLowerCase()]) {
      return named[colorStr.toLowerCase()];
    }

    return { r: 0, g: 0, b: 0, a: 1 };
  }

  public static lerpColor(c1: string, c2: string, t: number): string {
    const col1 = Interpolator.parseColor(c1);
    const col2 = Interpolator.parseColor(c2);

    const r = Math.round(Interpolator.lerp(col1.r, col2.r, t));
    const g = Math.round(Interpolator.lerp(col1.g, col2.g, t));
    const b = Math.round(Interpolator.lerp(col1.b, col2.b, t));
    const a = Interpolator.lerp(col1.a, col2.a, t);

    return `rgba(${r}, ${g}, ${b}, ${a.toFixed(3)})`;
  }

  /**
   * SVG Path Morphing Interpolator.
   * Splits path commands and linearly interpolates coordinates between two normalized paths.
   */
  public static lerpPath(pathA: string, pathB: string, t: number): string {
    const numbersA = pathA.match(/[-+]?[0-9]*\.?[0-9]+/g)?.map(Number) || [];
    const numbersB = pathB.match(/[-+]?[0-9]*\.?[0-9]+/g)?.map(Number) || [];

    if (numbersA.length === 0 || numbersB.length === 0) return t < 0.5 ? pathA : pathB;

    const maxLen = Math.max(numbersA.length, numbersB.length);
    const padA = [...numbersA];
    const padB = [...numbersB];

    while (padA.length < maxLen) padA.push(padA[padA.length - 1] || 0);
    while (padB.length < maxLen) padB.push(padB[padB.length - 1] || 0);

    let idx = 0;
    // Replace all numbers in template path with interpolated numbers
    const template = numbersA.length >= numbersB.length ? pathA : pathB;
    return template.replace(/[-+]?[0-9]*\.?[0-9]+/g, () => {
      const v = Interpolator.lerp(padA[idx] ?? 0, padB[idx] ?? 0, t);
      idx++;
      return v.toFixed(2);
    });
  }
}
